import { useCallback } from 'react';
import { useSitemap } from 'ws-scripts/modules/common';
import { useDrawer } from '../context/DrawerContext';
import { useDrawerNavigation } from '../context/DrawerNavigationContext';

/**
 * Sitemap alias for the CMS fragment page that embeds the site-text-search
 * widget's `panel` display mode. Compiled into this bundle, so it must match
 * — verbatim — the widget's own sitemap manifest and the DVS sitemap entry
 * pointing at it (PRD D13). A mismatch resolves to `null` and this hook does
 * nothing; there is no error or log.
 */
export const SEARCH_PANEL_ALIAS = 'DRAWER_SITE_TEXT_SEARCH_FRAGMENT';

/**
 * useSearchPanel — the single code path both search triggers (the drawer's
 * search field and the header magnifier) share to open the Level 2 search
 * panel (PRD D8). Resolves the sitemap alias to a fragment path and, only
 * when it resolves, opens the drawer and drills into a synthetic item
 * embedding that fragment — reusing the drilldown's slide, back button,
 * close button, focus trap, and keep-mounted behavior unchanged (PRD D1).
 *
 * Resolution happens here, in the React tree via useSitemap(), rather than
 * from server-side store state, where there is no sitemap promise and
 * reading it would race the in-flight fetch.
 *
 * @returns {(triggerRef?: ElementRef) => void} Call on trigger activation.
 *   `triggerRef` is forwarded to onSubmenuOpen — the element focus returns
 *   to when the panel closes.
 */
const useSearchPanel = () => {
	const sitemap = useSitemap();
	const { openDrawer } = useDrawer();
	const { onSubmenuOpen } = useDrawerNavigation();

	return useCallback(
		(triggerRef) => {
			const url = sitemap.get(SEARCH_PANEL_ALIAS);
			if (!url) {
				if (process.env.NODE_ENV !== 'production') {
					// eslint-disable-next-line no-console
					console.warn(
						`[ws-navigation-drawer] Search panel alias "${SEARCH_PANEL_ALIAS}" ` +
							'not found in sitemap. Add the corresponding DVS sitemap ' +
							'entry to enable the search panel.'
					);
				}
				return;
			}

			openDrawer();
			onSubmenuOpen(
				{
					id: SEARCH_PANEL_ALIAS,
					// No label: the panel's own visible "Search" heading (T6)
					// already identifies it, so a second category name would be
					// redundant — and no translation is registered for a label
					// id here anyway (PRD T19).
					label: '',
					navigationItems: [{ url, embedded: true }]
				},
				triggerRef
			);
		},
		[sitemap, openDrawer, onSubmenuOpen]
	);
};

export default useSearchPanel;
